import { Header } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useCallback } from "react";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import type {
  RebalanceAccount,
  RebalanceMetrics,
  RebalanceStatus,
  RebalanceType,
  RebalanceWithMetrics,
  Trade,
  TradeSide,
  WithPositionSwapId,
} from "../../../api/src/rebalances/rebalances.service";
import type { RiskAllocation } from "../../../api/src/securities/securities.service";
import MultiSelector from "../components/MultiSelector";
import {
  deserializeDate,
  processEmptyResponse,
  useAuthenticatedMutationNew,
  useAuthenticatedQuery,
} from "../lib/api";
import { deserializeRebalanceMetricPercents } from "./percents";

export const rebalanceTypeDisplay: Record<RebalanceType, string> = {
  tolerance: "Tolerance",
  // dca: "Dollar Cost Average",
  raise_cash: "Raise Cash",
  spend_cash: "Spend Cash",
  tlh: "TLH",
  manual: "Manual",
  position_swap: "Position swap",
};

export const rebalanceTypeFilterOptions = Object.values(
  rebalanceTypeDisplay,
).map((display, index) => ({
  label: display,
  value: index + 1,
}));

/**
 * `postRebalanceHoldings` with its two dates as `Date`s. Each is `null` for a
 * security the account buys and did not hold before (`PostRebalanceHolding`,
 * `api/src/rebalances/rebalances.service.ts`), and `null` stays `null`.
 */
function deserializePostRebalanceHoldings(
  holdings: SerializedObject<RebalanceMetrics>["postRebalanceHoldings"],
): RebalanceMetrics["postRebalanceHoldings"] {
  return (
    holdings?.map((holding) => ({
      ...holding,
      asOfDate: deserializeDate(holding.asOfDate),
      firstOpenDate: deserializeDate(holding.firstOpenDate),
    })) ?? null
  );
}

export function deserializeRebalance(
  rebalance: SerializedObject<RebalanceWithMetrics>,
): RebalanceWithMetrics {
  return {
    ...rebalance,
    lastTradeDate: deserializeDate(rebalance.lastTradeDate) ?? null,
    proposedExecutedTime: deserializeDate(rebalance.proposedExecutedTime),
    actualExecutedTime: deserializeDate(rebalance.actualExecutedTime) ?? null,
    createdTime: deserializeDate(rebalance.createdTime),
    updatedTime: deserializeDate(rebalance.updatedTime),
    dcaStartDate: deserializeDate(rebalance.dcaStartDate),
    dcaEndDate: deserializeDate(rebalance.dcaEndDate),
    postRebalanceHoldings: deserializePostRebalanceHoldings(
      rebalance.postRebalanceHoldings,
    ),
    accounts: rebalance.accounts.map(
      (account): RebalanceAccount & RebalanceMetrics => ({
        ...account,
        // `?? null`: a response from before the key existed carries none.
        cashBalanceOverride:
          account.cashBalanceOverride == null
            ? null
            : {
                ...account.cashBalanceOverride,
                time: new Date(account.cashBalanceOverride.time),
              },
        lastTradeDate: deserializeDate(account.lastTradeDate) ?? null,
        postRebalanceHoldings: deserializePostRebalanceHoldings(
          account.postRebalanceHoldings,
        ),
        trades: account.trades.map((trade): Trade => ({
          ...trade,
          executedTime: deserializeDate(trade.executedTime) ?? null,
          createdTime: deserializeDate(trade.createdTime),
          updatedTime: deserializeDate(trade.updatedTime),
        })),
      }),
    ),
  };
}

/**
 * The path, and so the react-query key, of `GET /v1/rebalances/:id/check`
 * (APP-1652: served at `/v1` only). `RebalanceCheckPanel` and `ChecksPanel`
 * read it, and `AdminRebalanceExplain` primes the same key from the
 * cross-firm bundle.
 */
export function rebalanceCheckPath(rebalanceId: number) {
  return `/v1/rebalances/${rebalanceId}/check`;
}

export function useQueryRebalance(
  id: number,
  { includeMetrics = false, includeTrades = false, enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<RebalancesController["getRebalance"]>,
    WithPositionSwapId<RebalanceWithMetrics>,
    [
      "v1",
      "rebalances",
      number,
      {
        includeMetrics: boolean;
        includeTrades: boolean;
      },
    ]
  >({
    // APP-1652: `GET /rebalances/:id` is served at `/v1` only.
    queryKey: ["v1", "rebalances", id, { includeMetrics, includeTrades }],
    // The metric percents on 0 to 1 (`./percents`). `positionSwapId` and
    // `positionSwapName` are the swap that owns a `position_swap` proposal
    // (APP-1486, R122).
    queryFn: async (rebalance) => ({
      ...deserializeRebalance(
        deserializeRebalanceMetricPercents(rebalance.data),
      ),
      positionSwapId: rebalance.data.positionSwapId ?? null,
      positionSwapName: rebalance.data.positionSwapName ?? null,
    }),
    urlBuilderFn: (key) =>
      `/${key[0]}/${key[1]}/${key[2]}/?metrics=${key[3].includeMetrics}&trades=${key[3].includeTrades}`,
    queryOptions: { enabled },
  });
}

export function getRiskAllocationDisplay(
  riskAllocation: RiskAllocation = {},
  sum = 100,
) {
  const normalized = getRiskAllocation(riskAllocation, sum);

  // Format as "XX / YY" for all risk levels (values only, no labels)
  return Object.entries(normalized)
    .map(([, value]) => {
      let displayValue = value * sum;
      displayValue =
        displayValue % 1 < 0.5
          ? Math.floor(displayValue)
          : Math.ceil(displayValue);
      return `${displayValue}`;
    })
    .join(" / ");
}

export function getRiskAllocation(
  riskAllocation: RiskAllocation = {},
  expectedSum = 100,
) {
  // Handle dynamic risk level keys (e.g., "Growth", "Defensive", or custom names)
  // Filter out non-risk-level categories like "Cash" and "Unknown"
  const excludeKeys = ["Cash", "Unknown", "cash", "unknown"];
  const result: Record<string, number> = {};
  let riskSum = 0;

  for (const [key, value] of Object.entries(riskAllocation)) {
    if (excludeKeys.includes(key)) continue;
    const intValue = Math.max(0, parseInt(((value ?? 0) * 100).toFixed(0)));
    result[key] = intValue;
    riskSum += intValue;
  }

  const reduction = riskSum === 0 ? 1 : expectedSum / riskSum;

  for (const key of Object.keys(result)) {
    result[key] *= reduction;
    result[key] /= expectedSum;
  }

  return result;
}

export const RebalanceReasonFilter = <TRow, TValue>({
  header,
}: {
  header: Header<TRow, TValue>;
}) => {
  const value = (header.column.getFilterValue() ?? []) as number[];

  const onChange = useCallback(
    (values: number[]) => {
      header.column.setFilterValue(values);
    },
    [header.column],
  );

  return (
    <MultiSelector
      options={rebalanceTypeFilterOptions}
      placeholder="All"
      setValue={onChange}
      value={value}
    />
  );
};

export type RebalanceRow = {
  // `swap`: the group row of the proposals of one position swap
  // (`groupPositionSwapRows`, APP-1486).
  level: "household" | "account" | "swap";
  rebalanceId: number;
  id: number;
  name: string;
  status: RebalanceStatus;
  type?: RebalanceType;
  accounts?: RebalanceRow[];
  actualExecutionDate?: Date;
  assignedUser?: {
    id: string;
    name: string;
  };
  assignedUserId?: string | null;
  // The swap whose proposal the rebalance is, `null` for a rebalance no swap
  // owns; the swap's own id on a `swap` row (APP-1486).
  positionSwapId?: number | null;
  // The name of that swap, `null` where `positionSwapId` is (R122).
  positionSwapName?: string | null;
} & RebalanceMetrics;

export function mapRebalancesToRebalanceRows(
  rebalances: (RebalanceWithMetrics &
    Partial<WithPositionSwapId<object>>)[] = [],
) {
  return rebalances.map((rebalance): RebalanceRow => ({
    ...rebalance,
    level: "household",
    rebalanceId: rebalance.id,
    id: rebalance.householdId,
    name: rebalance.householdName,
    assignedUserId: rebalance.assignedUserId,
    accounts: rebalance.accounts.map((account): RebalanceRow => ({
      ...account,
      level: "account",
      name: account.accountName,
      status: rebalance.status,
      rebalanceId: rebalance.id,
      type: rebalance.type, // Inherit parent's rebalance type for filtering
    })),
    actualExecutionDate:
      deserializeDate(rebalance.actualExecutedTime) ?? undefined,
  }));
}

/** The totals a `swap` row shows: the sum over its proposals. */
const SWAP_ROW_TOTALS = [
  "totalBalance",
  "cashBalance",
  "buyTotal",
  "sellTotal",
  "realizedGainLossSt",
  "realizedGainLossLt",
] as const;

/** The row id of the group row of swap `swapId`. */
export const positionSwapRowId = (swapId: number) => `swap-${swapId}`;

/**
 * `rows` with the proposals of each position swap moved under one `swap` row
 * that names the swap (APP-1486, swap spec R82). The `swap` row takes the
 * place of the first proposal of its swap; every other row keeps its place.
 * Its totals are the sums over its proposals, `null` where each proposal has
 * `null`; the rest of its metrics are `null`. The `swap` row takes the
 * `positionSwapName` of its proposals (R122); a swap no proposal names is
 * "Position swap <id>".
 */
export function groupPositionSwapRows(rows: RebalanceRow[]): RebalanceRow[] {
  const proposalsBySwap = _.groupBy(
    rows.filter((row) => row.positionSwapId != null),
    (row) => row.positionSwapId,
  );
  const grouped: RebalanceRow[] = [];
  for (const row of rows) {
    const swapId = row.positionSwapId;
    if (swapId == null) {
      grouped.push(row);
      continue;
    }
    const proposals = proposalsBySwap[swapId];
    if (proposals[0] !== row) continue;
    const totals = Object.fromEntries(
      SWAP_ROW_TOTALS.map((key) => {
        const values = proposals
          .map((proposal) => proposal[key])
          .filter((value): value is number => value != null);
        return [key, values.length === 0 ? null : _.sum(values)];
      }),
    ) as Pick<RebalanceMetrics, (typeof SWAP_ROW_TOTALS)[number]>;
    const swapName =
      proposals.find((proposal) => proposal.positionSwapName != null)
        ?.positionSwapName ?? null;
    grouped.push({
      level: "swap",
      // A swap row is no rebalance; the table keys it by `positionSwapRowId`.
      rebalanceId: Number.NaN,
      id: swapId,
      positionSwapId: swapId,
      positionSwapName: swapName,
      name: swapName ?? `Position swap ${swapId}`,
      status: row.status,
      type: "position_swap",
      accounts: proposals,
      reason: null,
      reasonChange: null,
      numTrades: null,
      checkStatus: null,
      checkScore: null,
      checkCritical: null,
      checkError: null,
      checkWarning: null,
      checkInfo: null,
      auditMaxSeverity: null,
      lastTradeDate: null,
      postRebalanceHoldings: null,
      assetAllocation: null,
      riskAllocation: null,
      assignedUserId: null,
      serviceTier: null,
      customFields: null,
      cashSettings: null,
      ...totals,
    });
  }
  return grouped;
}

/**
 * `realizedGainLossSt` plus `realizedGainLossLt`, the "Realized Gain/Loss" of
 * the rebalance lists and the "Realized Gains" of a rebalance. `null` when
 * both are `null`; when one is `null`, the other.
 */
export function getRealizedGainLoss({
  realizedGainLossSt,
  realizedGainLossLt,
}: Pick<RebalanceMetrics, "realizedGainLossSt" | "realizedGainLossLt">) {
  if (realizedGainLossSt == null && realizedGainLossLt == null) {
    return null;
  }
  return (realizedGainLossSt ?? 0) + (realizedGainLossLt ?? 0);
}

export function getSelectedRebalanceRows(
  rowSelection: Record<string, boolean>,
) {
  return _.uniq(
    Object.entries(rowSelection)
      .filter(([id, isSelected]) => isSelected)
      .map(([id]) => {
        const rebalanceIdSegment = id.substring(0, id.indexOf("-"));
        return parseInt(rebalanceIdSegment);
      })
      // A row that is no rebalance, a swap's group row (APP-1486), has no id.
      .filter((rebalanceId) => !Number.isNaN(rebalanceId)),
  );
}

export function useUpdateRebalance(rebalanceId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<RebalancesController["updateRebalance"]>
  >({
    // APP-1652: served at `/v1` only; answers `{ data }` with the id and
    // status of the rebalance.
    mutationKey: ["PUT", "v1", "rebalances", rebalanceId],
    mutationFn: (data) => {
      return data;
    },
    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "v1",
            "rebalances",
            rebalanceId,
            { includeMetrics: true, includeTrades: true },
          ],
        });
      },
    },
  });
}

export function useChangeRebalanceState() {
  return useAuthenticatedMutationNew<
    UnpackResponse<RebalancesController["changeRebalancesState"]>
  >({
    mutationKey: ["PATCH", "rebalances"],
    mutationFn: () => null,
    responseCallback: processEmptyResponse,
  });
}

/**
 * Get the positive or negative factor for a given trade's side.
 * @param direction The trade side
 * @returns 1 if buy; -1 if sell
 */
export function getTradeSideFactor(side: TradeSide) {
  return side === "buy" ? 1 : -1;
}
